/**
 * @graphql cc
 */
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';

import { useInlineCommentsState } from '@confluence/inline-comments-hooks/entry-points/useInlineComments';
import {
	CommentsSectionQuery,
	CommentsSectionWithoutReactionsQuery,
} from '@confluence/page-comments-queries/entry-points/CommentsSectionQuery.graphql';
import type {
	CommentsSectionQuery as CommentsSectionQueryType,
	CommentsSectionQueryVariables,
} from '@confluence/page-comments-queries/entry-points/__types__/CommentsSectionQuery';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';

import { useIsReactionsEnabled } from './useIsReactionsEnabled';

/**
 * @deprecated This type is deprecated and will be removed in a future version.
 * It was used to manage comment counts but is no longer needed as part of modernization.
 */
export type UseCommentCountsData = {
	unresolvedInlineCommentsCount: number;
	footerCommentsCount: number;
};

/**
 * @deprecated This hook is deprecated and will be removed in a future version.
 * It was used to fetch comment counts but is no longer needed as part of modernization.
 */
export const useCommentCountsLegacy = (contentId: string): UseCommentCountsData | undefined => {
	const { unresolvedInlineComments } = useInlineCommentsState();
	const isReactionsEnabled = useIsReactionsEnabled(contentId);

	// Instead of requesting 0 comments with the 'first' param here I am reusing the query that is preloaded and fetched
	// for page comments themselves. This allows me to take advantage of the caching and updates that occur in the
	// legacy classes. I've also just marked all errors as handled as we don't particularly care about the results here
	// and the page comments component has much more robust error handling than we'd want to add here.
	const { data: footerCommentsData, error } = useQuery<
		CommentsSectionQueryType,
		CommentsSectionQueryVariables
	>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- This is not preloaded but there is a name collision with a preloaded query
		isReactionsEnabled ? CommentsSectionQuery : CommentsSectionWithoutReactionsQuery,
		{
			variables: {
				contentId,
				offset: '',
				first: 100,
			},
			fetchPolicy:
				window.__SSR_RENDERED__ && fg('confluence_frontend_remove_comments_placeholder')
					? 'cache-first'
					: undefined,
			skip:
				fg('confluence_comment_use_active_comment_query') ||
				expVal('cc_comments_new_comments_data_manager', 'isEnabled', false) ||
				!contentId,
		},
	);

	if (error) {
		markErrorAsHandled(error);
	}

	// Return undefined when feature gate or experiment is enabled
	if (
		fg('confluence_comment_use_active_comment_query') ||
		expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
	) {
		return undefined;
	}

	return {
		unresolvedInlineCommentsCount:
			(unresolvedInlineComments && unresolvedInlineComments.length) ?? 0,
		footerCommentsCount: (footerCommentsData && footerCommentsData.comments?.totalCount) ?? 0,
	};
};
